import React, { useState } from 'react';
import type { TnmStaging, TnmStagingInput } from '../types/consultation.types';
import ConfirmModal from '../../../components/common/ConfirmModal';
import SectionNumberBadge from './SectionNumberBadge';
import Spinner from '../../../components/ui/Spinner';

interface Props {
  consultationId: number;
  existingStaging?: TnmStaging | null;
  onSave: (data: TnmStagingInput) => Promise<void>;
  onDelete?: () => Promise<void>;
  sectionNumber?: string;
}

const T_CATEGORIES = [
  { value: 'tx', label: 'TX - Tumor primario no evaluable' },
  { value: 't0', label: 'T0 - Sin evidencia de tumor primario' },
  { value: 'tis', label: 'Tis - Carcinoma in situ' },
  { value: 't1', label: 'T1 - Tumor de menor tamaño/invasión' },
  { value: 't2', label: 'T2 - Tumor de tamaño/invasión intermedio' },
  { value: 't3', label: 'T3 - Tumor de mayor tamaño/invasión' },
  { value: 't4', label: 'T4 - Tumor invade estructuras adyacentes' },
];

const T_SUBCATEGORIES = [
  { value: 't1a', label: 'T1a' },
  { value: 't1b', label: 'T1b' },
  { value: 't1c', label: 'T1c' },
  { value: 't2a', label: 'T2a' },
  { value: 't2b', label: 'T2b' },
  { value: 't2c', label: 'T2c' },
  { value: 't3a', label: 'T3a' },
  { value: 't3b', label: 'T3b' },
  { value: 't4a', label: 'T4a' },
  { value: 't4b', label: 'T4b' },
];

const N_CATEGORIES = [
  { value: 'nx', label: 'NX - Ganglios linfáticos regionales no evaluables' },
  { value: 'n0', label: 'N0 - Sin metástasis en ganglios linfáticos regionales' },
  { value: 'n1', label: 'N1 - Metástasis en ganglios linfáticos regionales' },
  { value: 'n2', label: 'N2 - Metástasis regional moderada' },
  { value: 'n3', label: 'N3 - Metástasis regional extensa' },
];

const N_SUBCATEGORIES = [
  { value: 'n1a', label: 'N1a' },
  { value: 'n1b', label: 'N1b' },
  { value: 'n1c', label: 'N1c' },
  { value: 'n2a', label: 'N2a' },
  { value: 'n2b', label: 'N2b' },
  { value: 'n2c', label: 'N2c' },
  { value: 'n3a', label: 'N3a' },
  { value: 'n3b', label: 'N3b' },
  { value: 'n3c', label: 'N3c' },
];

const M_CATEGORIES = [
  { value: 'm0', label: 'M0 - Sin metástasis distante' },
  { value: 'm1', label: 'M1 - Metástasis distante presente' },
];

export default function TnmStagingForm({ consultationId, existingStaging, onSave, onDelete, sectionNumber }: Props) {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<TnmStagingInput>({
    t_category: existingStaging?.t_category ?? null,
    t_details: existingStaging?.t_details ?? null,
    n_category: existingStaging?.n_category ?? null,
    n_details: existingStaging?.n_details ?? null,
    m_category: existingStaging?.m_category ?? null,
    clinical_stage_group: existingStaging?.clinical_stage_group ?? null,
    is_pathological: existingStaging?.is_pathological ?? false,
    staging_date: existingStaging?.staging_date ?? null,
    notes: existingStaging?.notes ?? null,
  });

  const handleChange = (field: keyof TnmStagingInput, value: string | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar la estadificación TNM');
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = () => {
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleConfirmDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete();
      setShowDeleteConfirm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar la estadificación TNM');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <h4 className="card-title">
          {sectionNumber && <SectionNumberBadge number={sectionNumber} />}
          <i className="fa fa-heartbeat me-2"></i>
          Estadificación Oncológica TNM
        </h4>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button type="button" className="btn-close" onClick={() => setError(null)}></button>
            </div>
          )}

          {/* T Category */}
          <div className="row mt-3">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">T (Tumor Primario)</label>
                <select
                  className="form-select"
                  value={formData.t_category ?? ''}
                  onChange={(e) => handleChange('t_category', e.target.value || null)}
                >
                  <option value="">-- Seleccione categoría T --</option>
                  {T_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* T Details */}
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Subcategoría T (Detalles)</label>
                <select
                  className="form-select"
                  value={formData.t_details ?? ''}
                  onChange={(e) => handleChange('t_details', e.target.value || null)}
                  disabled={!formData.t_category}
                >
                  <option value="">-- Seleccione subcategoría T (opcional) --</option>
                  {T_SUBCATEGORIES.map(sub => (
                    <option key={sub.value} value={sub.value}>{sub.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* N Category */}
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">N (Ganglios Linfáticos Regionales)</label>
                <select
                  className="form-select"
                  value={formData.n_category ?? ''}
                  onChange={(e) => handleChange('n_category', e.target.value || null)}
                >
                  <option value="">-- Seleccione categoría N --</option>
                  {N_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* N Details */}
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Subcategoría N (Detalles)</label>
                <select
                  className="form-select"
                  value={formData.n_details ?? ''}
                  onChange={(e) => handleChange('n_details', e.target.value || null)}
                  disabled={!formData.n_category}
                >
                  <option value="">-- Seleccione subcategoría N (opcional) --</option>
                  {N_SUBCATEGORIES.map(sub => (
                    <option key={sub.value} value={sub.value}>{sub.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* M Category */}
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">M (Metástasis Distante)</label>
                <select
                  className="form-select"
                  value={formData.m_category ?? ''}
                  onChange={(e) => handleChange('m_category', e.target.value || null)}
                >
                  <option value="">-- Seleccione categoría M --</option>
                  {M_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Clinical Stage Group */}
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Grupo de Estadio Clínico</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.clinical_stage_group ?? ''}
                  onChange={(e) => handleChange('clinical_stage_group', e.target.value || null)}
                  placeholder="ej., I, II, III, IV, 0"
                  maxLength={20}
                />
              </div>
            </div>
          </div>

          {/* Is Pathological & Staging Date */}
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Fecha de Estadificación</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.staging_date ?? ''}
                  onChange={(e) => handleChange('staging_date', e.target.value || null)}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Tipo de Estadificación</label>
                <div className="form-check mt-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isPathological"
                    checked={formData.is_pathological ?? false}
                    onChange={(e) => handleChange('is_pathological', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="isPathological">
                    Estadificación Patológica (vs. Estadificación Clínica)
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="row">
            <div className="col-12">
              <div className="mb-3">
                <label className="form-label">Notas Clínicas</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.notes ?? ''}
                  onChange={(e) => handleChange('notes', e.target.value || null)}
                  placeholder="Notas, observaciones o justificación adicional de la estadificación..."
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="row">
            <div className="col-12">
              <div className="d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <Spinner size="sm" text="Guardando..." />
                  ) : (
                    <>
                      <i className="fa fa-save me-1"></i>
                      {existingStaging ? 'Actualizar Estadificación' : 'Guardar Estadificación'}
                    </>
                  )}
                </button>
                {existingStaging && onDelete && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={requestDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <Spinner size="sm" text="Eliminando..." />
                    ) : (
                      <>
                        <i className="fa fa-trash me-1"></i>
                        Eliminar Estadificación
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      <ConfirmModal
        show={showDeleteConfirm && !!onDelete}
        title="Eliminar estadificación TNM"
        body="¿Está seguro que desea eliminar esta estadificación TNM? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        confirmVariant="danger"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}
